// ===========================================
// Student View Dashboard (Parent Mode)
// ===========================================
// Renders the same StudentDashboardContent as the student dashboard,
// but fetches data via parent-accessible hooks.

import { Box, Skeleton } from '@mui/material';
import { useMyChildEnrollments, useUpdateChildAvatar } from '../../../hooks/useParentProfile';
import { useNotesByStudent } from '../../../hooks/useNotes';
import { useResourcesByStudent } from '../../../hooks/useResources';
import { useViewTerms } from '../../../hooks/useViewTerms';
import { useStudentView } from '../../../contexts/useStudentView';
import StudentDashboardContent from '../../student/StudentDashboardContent';
import type { StudentDashboardProfile } from '../../../api/user.api';
import type { AgeGroup } from '../../brand';

interface StudentChild {
  id: string;
  firstName: string;
  lastName: string;
  birthDate: string;
  avatar: string | null;
  attendanceRate: number | null;
  enrollmentCount: number;
  /** False for a withdrawn child: shown read-only (#457). */
  isActive?: boolean;
}

interface Props {
  student: StudentChild;
  characterColor: string;
  ageGroup: AgeGroup;
}

export default function StudentViewDashboard({ student }: Props) {
  const { updateStudentAvatar } = useStudentView();
  const updateChildAvatar = useUpdateChildAvatar();

  const { data: enrollments = [], isLoading: enrollmentsLoading } = useMyChildEnrollments(student.id);
  const { data: notes = [], isLoading: notesLoading } = useNotesByStudent(student.id);
  const { data: resources = [], isLoading: resourcesLoading } = useResourcesByStudent(student.id);
  const { data: terms = [] } = useViewTerms(); // a parent cannot read /admin/terms (#272)

  if (enrollmentsLoading) {
    return (
      <Box sx={{ py: 4 }}>
        <Skeleton variant="rectangular" width="100%" height={400} />
      </Box>
    );
  }

  const profile: StudentDashboardProfile = {
    id: student.id,
    firstName: student.firstName,
    lastName: student.lastName,
    birthDate: student.birthDate,
    avatar: student.avatar,
    ageGroup: null,
    family: null,
    enrollmentCount: student.enrollmentCount,
    attendanceRate: student.attendanceRate,
  };

  return (
    <StudentDashboardContent
      profile={profile}
      enrollments={enrollments}
      enrollmentsLoading={enrollmentsLoading}
      notes={notes}
      notesLoading={notesLoading}
      resources={resources}
      resourcesLoading={resourcesLoading}
      terms={terms}
      // A withdrawn child is read-only: the server refuses edits for them (#457).
      onAvatarChange={
        student.isActive === false
          ? undefined
          : (avatar) => {
              updateStudentAvatar(avatar);
              updateChildAvatar.mutate({ studentId: student.id, avatar });
            }
      }
      avatarChangePending={updateChildAvatar.isPending}
      isParentView
    />
  );
}
